import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Loader2, XCircle, CreditCard } from 'lucide-react';
import { useTheme } from '../zustand/ThemeStore';
import { useHierarchicalUsersStore } from '../zustand/HierarchicalUsersStore';
import { useAuthorization } from '../hooks/useAuthorization';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/config';
import { paymentService } from '../services/payment';
import { 
  StatsCards, 
  AddUserForm, 
  UserTable, 
  ConfirmationModal, 
  EditUserModal 
} from '../components/settings';
import { CancellationFeedbackModal } from '../components/CancellationFeedbackModal';
import type { UserType, EditData } from '../types/settings';

const SettingsPage = () => {
  // Use hierarchical users store instead of local state
  const { users, isLoading: usersLoading, error: usersError, fetchUsers: fetchHierarchicalUsers } = useHierarchicalUsersStore();
  
  // Track initial loading state
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Authorization hooks
  const auth = useAuthorization();
  const darkMode = useTheme((state) => state.theme);
  
  // Modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUser, setPendingUser] = useState<UserType | null>(null);
  const [pendingAction, setPendingAction] = useState<'revoke' | 'grant' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserType | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  
  const [refreshing, setRefreshing] = useState(false);
  
  // Cancellation modal state
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  
  // Billing portal loading state
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  // Check if user has permission to access settings
  if (!auth.canAccessSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Access Denied
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try {
        await fetchHierarchicalUsers();
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
  }, [fetchHierarchicalUsers]);

  const refreshUsers = async () => {
    setRefreshing(true);
    try {
      await fetchHierarchicalUsers();
    } catch (err) {
      console.error('Failed to refresh users:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleUserAdded = () => {
    // Refresh users from the store to get updated data
    fetchHierarchicalUsers();
  };

  const handleActionClick = (user: UserType) => {
    setPendingUser(user);
    setPendingAction(user.status === 'unsuspended' ? 'revoke' : 'grant');
    setShowConfirmModal(true);
    setActionError('');
  };

  const confirmUserStatusChange = async () => {
    if (!pendingUser || !pendingAction) return;
    
    setActionLoading(true);
    setActionError('');
    
    try {
      const token = localStorage.getItem("token");
      const endpoint = pendingAction === 'revoke' 
        ? `${getApiBaseUrl()}/api/v1/users/suspend`
        : `${getApiBaseUrl()}/api/v1/users/unsuspend`;
      
      const response = await axios.post(endpoint, {
        uid: pendingUser.uid
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        toast.success(
          `User ${pendingAction === 'revoke' ? 'suspended' : 'unsuspended'} successfully`
        );
        
        setShowConfirmModal(false);
        setPendingUser(null);
        setPendingAction(null);
        
        // Refresh users from the store to get updated data
        refreshUsers();
      } else {
        setActionError(`Failed to ${pendingAction} user access`);
        toast.error(`Failed to ${pendingAction} user access`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || `Failed to ${pendingAction} user access`;
      setActionError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const cancelUserStatusChange = () => {
    setShowConfirmModal(false);
    setPendingUser(null);
    setPendingAction(null);
    setActionError('');
  };

  const handleEditClick = (user: UserType) => {
    setEditUser(user);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (editData: EditData) => {
    if (!editUser) return;
    setEditLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${getApiBaseUrl()}/api/v1/users/update/${editUser.uid}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User details updated');
      setEditModalOpen(false);
      setEditUser(null);
      refreshUsers();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditUser(null);
  };

  const handleManageBilling = async () => {
    setBillingPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to manage your subscription');
        return;
      }

      const response = await paymentService.createBillingPortalSession(token);
      
      if (response.success && response.portal_url) {
        window.location.href = response.portal_url;
      } else {
        toast.error('Failed to open billing portal');
      }
    } catch (error: any) {
      console.error('Error opening billing portal:', error);
      toast.error(error.message || 'Failed to open billing portal');
    } finally {
      setBillingPortalLoading(false);
    }
  };

  // Show loader while initial data is loading
  if (initialLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-black' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <Loader2 className={`mx-auto h-12 w-12 animate-spin ${
            darkMode ? 'text-white' : 'text-[#1a9f6b]'
          }`} />
          <p className={`mt-4 text-lg font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Loading settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-black' : 'bg-gray-50'
    } p-3 sm:p-6 md:p-8`}>
      <div className="max-w-6xl mx-auto">
        {/* Page Title */}
        <div className="mb-6 sm:mb-8">
          <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-2 ${
            darkMode ? 'text-white' : 'text-[#053333]'
          }`}>
            User Management
          </h1>
          <p className={`text-sm sm:text-base ${
            darkMode ? 'text-gray-400' : 'text-[#373737]'
          }`}>
            Manage team members and their access permissions
          </p>
        </div>

        {/* Stats Cards */}
        <StatsCards users={users} />

        {/* Add User Section */}
        <AddUserForm onUserAdded={handleUserAdded} />

        {/* Users List */}
        <UserTable
          users={users}
          isLoading={usersLoading}
          error={usersError || ''}
          onRefresh={refreshUsers}
          onActionClick={handleActionClick}
          onEditClick={handleEditClick}
          refreshing={refreshing}
          actionLoading={actionLoading}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          user={pendingUser}
          action={pendingAction}
          loading={actionLoading}
          error={actionError}
          onConfirm={confirmUserStatusChange}
          onCancel={cancelUserStatusChange}
        />

        {/* Edit User Modal */}
        <EditUserModal
          isOpen={editModalOpen}
          user={editUser}
          loading={editLoading}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
        />

        {/* Manage Billing Section */}
        <div className={`rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mt-6 sm:mt-8 
          transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] ${
          darkMode 
            ? 'bg-[#0a1414] border border-[#1a2424] hover:border-[#1a9f6b]/30' 
            : 'bg-white border border-gray-200 hover:border-[#1a9f6b]/30'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg sm:text-xl font-semibold ${
              darkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Manage Subscription
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <p className={`text-sm sm:text-base ${
                darkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                Update your payment methods, view billing history, and manage your subscription plan through our secure billing portal.
              </p>
            </div>
            <button
              onClick={handleManageBilling}
              disabled={billingPortalLoading}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-300 
                flex items-center justify-center space-x-2 whitespace-nowrap shadow-md hover:shadow-lg
                ${billingPortalLoading ? 'opacity-50 cursor-not-allowed' : ''}
                ${darkMode 
                  ? 'bg-[#1a9f6b] hover:bg-[#148856] text-white hover:scale-105' 
                  : 'bg-[#1a9f6b] hover:bg-[#148856] text-white hover:scale-105'
                }`}
            >
              {billingPortalLoading ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  <span className="text-sm sm:text-base">Opening Portal...</span>
                </>
              ) : (
                <>
                  <CreditCard size={18} className="sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Manage Billing</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Cancel Subscription Section */}
        <div className={`rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mt-6 sm:mt-8 
          transition-all duration-300 hover:shadow-2xl hover:scale-[1.01] ${
          darkMode 
            ? 'bg-[#0a1414] border border-[#1a2424] hover:border-red-500/30' 
            : 'bg-white border border-gray-200 hover:border-red-500/30'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg sm:text-xl font-semibold ${
              darkMode ? 'text-white' : 'text-[#053333]'
            }`}>
              Cancel Subscription
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <p className={`text-sm sm:text-base ${
                darkMode ? 'text-gray-400' : 'text-[#373737]'
              }`}>
                We're sorry to see you go. If you'd like to cancel your subscription, please let us know why.
                Your feedback helps us improve our service.
              </p>
            </div>
            <button
              onClick={() => setShowCancellationModal(true)}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-300 
                flex items-center justify-center space-x-2 whitespace-nowrap shadow-md hover:shadow-lg
                ${darkMode 
                  ? 'bg-red-600 hover:bg-red-700 text-white hover:scale-105' 
                  : 'bg-red-600 hover:bg-red-700 text-white hover:scale-105'
                }`}
            >
              <XCircle size={18} className="sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Cancel Subscription</span>
            </button>
          </div>
        </div>

        {/* Cancellation Feedback Modal */}
        <CancellationFeedbackModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onSuccess={() => {
            // Optionally refresh user data or redirect
            toast.success('Subscription cancellation scheduled');
          }}
        />
      </div>
    </div>
  );
};

export default SettingsPage;
